/**
 * 작성창의 대용량 첨부 목록.
 *
 * 일반 첨부(AttachmentDropzone)와 별도 UI 인 이유는 보여줄 정보가 다르기 때문이다.
 *   - 업로드 진행률 — 2GB 는 수십 분 걸린다. 진행률이 없으면 멈춘 줄 알고 창을 닫는다.
 *   - 다운로드 가능기간 — 일반 첨부는 "제한없음"(메일함 안), 대용량은 만료일(공개 링크).
 *     이 한 칸이 경고 배너를 대신한다. 사용자가 "어떤 게 링크로 나가는지" 를 상시로 본다.
 *   - 재시도 — 실패해도 첨부를 다시 고르지 않게 한다.
 */
import React from "react";
import { Box, IconButton, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { formatFileSize as formatBytes } from "../../../../util/numberFormat";
import { LARGE_STATUS } from "./useLargeAttachments";

const fmtEta = (sec) => {
    if (sec == null) return "";
    if (sec < 60) return `${sec}초 남음`;
    const m = Math.floor(sec / 60);
    return m < 60 ? `${m}분 남음` : `${Math.floor(m / 60)}시간 ${m % 60}분 남음`;
};

const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : null);

export default function LargeAttachmentList({ items, quota, onRemove, onRetry }) {
    // 서버 설정을 그대로 쓴다. 기본값은 아직 quota 응답이 도착하기 전 한 프레임용일 뿐이다.
    const retentionDays = quota?.retentionDays ?? 30;
    const maxDownloads  = quota?.maxDownloadCount ?? 100;
    if (!items?.length) return null;

    return (
        <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>
            {items.map((it, idx) => {
                // QUEUED 도 포함한다. 빼면 대기 중인 항목이 파일명만 덩그러니 남아
                // '멈춘 것' 과 '순서를 기다리는 것' 을 구분할 수 없다(업로드는 순차라 대기가 흔하다).
                const busy = it.status === LARGE_STATUS.QUEUED
                          || it.status === LARGE_STATUS.UPLOADING
                          || it.status === LARGE_STATUS.PRESIGNING
                          || it.status === LARGE_STATUS.COMPLETING;
                const failed = it.status === LARGE_STATUS.ERROR;

                return (
                    <Box key={it.key} sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        px: 1.5, py: 1,
                        borderTop: idx === 0 ? "none" : "1px solid #f1f5f9",
                        bgcolor: failed ? "#fff7ed" : "transparent",
                    }}>
                        {/* 파일명 + 진행 상태 */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{
                                fontSize: 13, color: "#1e293b",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                                {it.filename}
                            </Typography>

                            {busy && (
                                <Box sx={{ mt: 0.5 }}>
                                    <LinearProgress
                                        variant={it.status === LARGE_STATUS.UPLOADING ? "determinate" : "indeterminate"}
                                        // 대기 중엔 막대가 흐르지 않게 — 진행 중으로 오해하지 않도록
                                        {...(it.status === LARGE_STATUS.QUEUED ? { variant: "determinate", value: 0 } : {})}
                                        value={it.progress || 0}
                                        sx={{ height: 4, borderRadius: 2 }}
                                    />
                                    <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.25 }}>
                                        {it.status === LARGE_STATUS.QUEUED     && "대기 중…"}
                                        {it.status === LARGE_STATUS.PRESIGNING && "업로드 준비 중…"}
                                        {it.status === LARGE_STATUS.COMPLETING && "마무리 중…"}
                                        {it.status === LARGE_STATUS.UPLOADING &&
                                            `${it.progress || 0}%${it.etaSec ? ` · ${fmtEta(it.etaSec)}` : ""}`}
                                    </Typography>
                                </Box>
                            )}

                            {failed && (
                                <Typography sx={{ fontSize: 11, color: "#c2410c", mt: 0.25 }}>
                                    {it.error || "업로드에 실패했습니다."}
                                </Typography>
                            )}
                        </Box>

                        {/* 용량 */}
                        <Typography sx={{ fontSize: 12, color: "#64748b", flexShrink: 0, minWidth: 68, textAlign: "right" }}>
                            {formatBytes(it.sizeBytes)}
                        </Typography>

                        {/*
                          다운로드 가능기간 — 일반 첨부와의 차이를 드러내는 유일한 표시.
                          발송 전에는 만료일이 확정되지 않는다(발송일 기준으로 계산되므로).
                        */}
                        <Typography sx={{ fontSize: 12, color: "#64748b", flexShrink: 0, minWidth: 108, textAlign: "right" }}>
                            {it.status === LARGE_STATUS.DONE
                                ? (fmtDate(it.expiresAt) ? `~${fmtDate(it.expiresAt)}` : `발송 후 ${retentionDays}일`)
                                : "—"}
                        </Typography>

                        {/* 액션 */}
                        <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                            {/* 파일 참조가 살아 있는 항목만 재시도할 수 있다 —
                                초안 복원분은 File 객체가 없어 다시 올리면 0바이트가 된다. */}
                            {failed && it.retryable && (
                                <Tooltip title="이어서 다시 시도">
                                    <IconButton size="small" onClick={() => onRetry?.(it.key)}>
                                        <RefreshRoundedIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title={busy ? "업로드 취소" : "첨부 제거"}>
                                <IconButton size="small" onClick={() => onRemove?.(it.key)}>
                                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>
                );
            })}

            <Box sx={{ px: 1.5, py: 0.75, bgcolor: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>
                <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                    대용량 첨부는 <b>{retentionDays}일 보관 / {maxDownloads}회 다운로드</b> 가능하며,
                    메일 본문에 <b>링크</b>로 들어갑니다. 링크를 가진 누구나 받을 수 있습니다.
                </Typography>
            </Box>
        </Box>
    );
}
